// Tiny ECS. Entities are integers; components are stored in typed maps
// keyed by entity id. Systems iterate components directly.
//
// Design choices:
//   - No archetype tables; just Map<EntityId, Component>. Simple, fast enough
//     for v1 entity counts (< 1500 total). Trade-off: iteration is O(n) over
//     the component map, not over a packed array.
//   - Entity ids are reused after destruction via a free-list. A version
//     counter packed into the high bits would catch stale references but is
//     not needed yet.
//   - World owns all component stores. Systems are plain functions taking
//     the world plus a dt.

export type EntityId = number;

export interface ComponentStore<C> {
  get(id: EntityId): C | undefined;
  set(id: EntityId, value: C): void;
  delete(id: EntityId): boolean;
  has(id: EntityId): boolean;
  forEach(fn: (value: C, id: EntityId) => void): void;
  readonly size: number;
}

class MapStore<C> implements ComponentStore<C> {
  private readonly data = new Map<EntityId, C>();

  get(id: EntityId): C | undefined {
    return this.data.get(id);
  }
  set(id: EntityId, value: C): void {
    this.data.set(id, value);
  }
  delete(id: EntityId): boolean {
    return this.data.delete(id);
  }
  has(id: EntityId): boolean {
    return this.data.has(id);
  }
  forEach(fn: (value: C, id: EntityId) => void): void {
    this.data.forEach(fn);
  }
  get size(): number {
    return this.data.size;
  }
}

export class World {
  private nextId: EntityId = 1;
  private readonly free: EntityId[] = [];
  private readonly alive = new Set<EntityId>();
  private readonly stores = new Map<string, MapStore<unknown>>();

  /** Allocate a new entity id. */
  createEntity(): EntityId {
    const id = this.free.pop() ?? this.nextId++;
    this.alive.add(id);
    return id;
  }

  /** Mark entity dead; remove all components. */
  destroyEntity(id: EntityId): void {
    if (!this.alive.has(id)) return;
    this.stores.forEach((store) => store.delete(id));
    this.alive.delete(id);
    this.free.push(id);
  }

  isAlive(id: EntityId): boolean {
    return this.alive.has(id);
  }

  /** Get (or lazily create) a typed component store. */
  store<C>(key: string): ComponentStore<C> {
    let s = this.stores.get(key);
    if (!s) {
      s = new MapStore<unknown>();
      this.stores.set(key, s);
    }
    return s as unknown as ComponentStore<C>;
  }

  get entityCount(): number {
    return this.alive.size;
  }
}
